import { Agenda, EsFileState, Role, UIElement } from '@enums';
import esFileAccessJson from './access-map/es_file_access.json';

export type DecisionEsFileAccessLevel =
  | 'none'
  | 'read'
  | 'write'
  | 'write_own'
  | 'execute'
  | 'execute_own';

export type DecisionEsFileAccessKey =
  | 'processingType'
  | 'proposers'
  | 'proposerContact'
  | 'proposerType'
  | 'proposerDistinction'
  | 'accessoryParticipants'
  | 'legalAdvisers'
  | 'challengedActs'
  | 'proceedingSubjects'
  | 'subjectIndexes'
  | 'regulations'
  | 'judge'
  | 'indexUserRole'
  | 'secretaryUserRole'
  | 'assistantUserRole'
  | 'analystUserRole'
  | 'decidingAuthority';

export type DecisionEsFileElement =
  | DecisionEsFileAccessKey
  | 'assistantUserRoles';

export type DecisionEsFileStateAccessMap = Partial<
  Record<DecisionEsFileAccessKey, DecisionEsFileAccessLevel>
>;

export type DecisionEsFileAgendaAccessMap = Partial<
  Record<EsFileState, DecisionEsFileStateAccessMap>
>;

export type DecisionEsFileRoleAccessMap = Partial<
  Record<Agenda, DecisionEsFileAgendaAccessMap>
>;

export type DecisionEsFileAccessMap = Partial<
  Record<Role, DecisionEsFileRoleAccessMap>
>;

type RawDecisionEsFileAccessMap = Record<
  string,
  Record<string, Record<string, Record<string, DecisionEsFileAccessLevel>>>
>;

const roleByAccessKey: Record<string, Role> = {
  lustrum: Role.LUSTRUM,
  head_osv: Role.HEAD_OSV,
  index: Role.INDEX,
  lawyer: Role.LAWYER,
  secretary: Role.SECRETARY,
  judge: Role.JUDGE,
  assistant: Role.ASSISTANT,
  file_room: Role.FILE_ROOM,
  analyst: Role.ANALYST,
  anonymization: Role.ANONYMIZATION,
  publication: Role.PUBLICATION,
  reader: Role.READER,
  adviser: Role.ADVISER,
};

const agendaByAccessKey: Record<string, Agenda> = {
  court: Agenda.COURT,
};

const esFileStateByAccessKey: Record<string, EsFileState> = {
  new: EsFileState.NEW,
  register_new: EsFileState.REGISTER_NEW,
  index: EsFileState.INDEX,
  register: EsFileState.REGISTER,
  judge_office: EsFileState.JUDGE_OFFICE,
  registry: EsFileState.REGISTRY,
  closed: EsFileState.CLOSED,
  in_progress: EsFileState.IN_PROGRESS,
  proposed: EsFileState.PROPOSED,
  accepted: EsFileState.ACCEPTED,
  rejected: EsFileState.REJECTED,
  withdrawn: EsFileState.WITHDRAWN,
};

export const decisionEsFileElementToAccessKey: Record<
  DecisionEsFileElement,
  DecisionEsFileAccessKey | undefined
> = {
  processingType: 'processingType',
  proposers: 'proposers',
  proposerContact: 'proposerContact',
  proposerType: 'proposerType',
  proposerDistinction: 'proposerDistinction',
  accessoryParticipants: 'accessoryParticipants',
  legalAdvisers: 'legalAdvisers',
  challengedActs: 'challengedActs',
  proceedingSubjects: 'proceedingSubjects',
  subjectIndexes: 'subjectIndexes',
  regulations: 'regulations',
  judge: 'judge',
  indexUserRole: 'indexUserRole',
  secretaryUserRole: 'secretaryUserRole',
  assistantUserRole: 'assistantUserRole',
  assistantUserRoles: 'assistantUserRole',
  analystUserRole: 'analystUserRole',
  decidingAuthority: 'decidingAuthority',
};

const courtDecisionEsFileElementToUiElement: Record<
  DecisionEsFileElement,
  string | undefined
> = {
  processingType: UIElement.CourtFile.PROCESSING_TYPE,
  proposers: UIElement.CourtFile.PROPOSERS,
  proposerContact: UIElement.CourtFile.PROPOSERS,
  proposerType: UIElement.CourtFile.PROPOSERS,
  proposerDistinction: UIElement.CourtFile.PROPOSERS,
  accessoryParticipants: UIElement.CourtFile.ACCESSORY_PARTICIPANTS,
  legalAdvisers: UIElement.CourtFile.LEGAL_ADVISERS,
  challengedActs: UIElement.CourtFile.CHALLENGED_ACTS,
  proceedingSubjects: UIElement.EsFileCommon.PROCEEDINGS_SUBJECTS,
  subjectIndexes: UIElement.EsFileCommon.SUBJECT_INDEXES,
  regulations: UIElement.EsFileCommon.REGULATIONS,
  judge: UIElement.CourtFile.JUDGE,
  indexUserRole: UIElement.CourtFile.INDEX,
  secretaryUserRole: UIElement.CourtFile.SECRETARY,
  assistantUserRole: UIElement.CourtFile.ASSISTANT,
  assistantUserRoles: UIElement.CourtFile.ASSISTANT,
  analystUserRole: UIElement.CourtFile.ANALYST,
  decidingAuthority: UIElement.CourtFile.DECIDING_AUTHORITY_MEMBERS,
};

const plenaryOpinionDecisionEsFileElementToUiElement: Record<
  DecisionEsFileElement,
  string | undefined
> = {
  processingType: UIElement.PlenaryOpinionFile.PROCESSING_TYPE,
  proposers: undefined,
  proposerContact: undefined,
  proposerType: undefined,
  proposerDistinction: undefined,
  accessoryParticipants: undefined,
  legalAdvisers: undefined,
  challengedActs: undefined,
  proceedingSubjects: UIElement.EsFileCommon.PROCEEDINGS_SUBJECTS,
  subjectIndexes: UIElement.EsFileCommon.SUBJECT_INDEXES,
  regulations: UIElement.EsFileCommon.REGULATIONS,
  judge: UIElement.PlenaryOpinionFile.JUDGE,
  indexUserRole: UIElement.PlenaryOpinionFile.INDEX_USER_ROLE,
  secretaryUserRole: undefined,
  assistantUserRole: undefined,
  assistantUserRoles: undefined,
  analystUserRole: UIElement.PlenaryOpinionFile.ANALYST_USER_ROLE,
  decidingAuthority: undefined,
};

export function getDecisionEsFileUiElement(
  agenda: Agenda | undefined,
  element: DecisionEsFileElement
): string | undefined {
  switch (agenda) {
    case Agenda.COURT:
      return courtDecisionEsFileElementToUiElement[element];
    case Agenda.PLENARY_OPINION:
      return plenaryOpinionDecisionEsFileElementToUiElement[element];
    default:
      return undefined;
  }
}

export function isDecisionEsFileReadableAccess(
  level?: DecisionEsFileAccessLevel
): boolean {
  return ['read', 'write', 'write_own', 'execute', 'execute_own'].includes(
    level ?? 'none'
  );
}

export function isDecisionEsFileWritableAccess(
  level?: DecisionEsFileAccessLevel
): boolean {
  return level === 'write' || level === 'write_own';
}

function normalizeDecisionEsFileAccessMap(
  rawMap: RawDecisionEsFileAccessMap
): DecisionEsFileAccessMap {
  return Object.entries(rawMap).reduce<DecisionEsFileAccessMap>(
    (roleMap, [rawRole, rawAgendaMap]) => {
      const role = roleByAccessKey[rawRole];

      if (!role) {
        return roleMap;
      }

      roleMap[role] = Object.entries(
        rawAgendaMap
      ).reduce<DecisionEsFileRoleAccessMap>(
        (agendaMap, [rawAgenda, rawStateMap]) => {
          const agenda = agendaByAccessKey[rawAgenda];

          if (!agenda) {
            return agendaMap;
          }

          agendaMap[agenda] = Object.entries(
            rawStateMap
          ).reduce<DecisionEsFileAgendaAccessMap>(
            (stateMap, [rawState, rawAccessMap]) => {
              const state = esFileStateByAccessKey[rawState];

              if (!state) {
                return stateMap;
              }

              stateMap[state] = rawAccessMap as DecisionEsFileStateAccessMap;

              return stateMap;
            },
            {}
          );

          return agendaMap;
        },
        {}
      );

      return roleMap;
    },
    {}
  );
}

// Legacy kompatibilitní export pro původní decision JSON model.
// Není zdrojem pravdy pro editaci polí uložených v decision.esFile;
// ta se mapují na spisové UIElement oprávnění přes getDecisionEsFileUiElement.
export const decisionEsFileAccessMap: DecisionEsFileAccessMap =
  normalizeDecisionEsFileAccessMap(
    esFileAccessJson as RawDecisionEsFileAccessMap
  );
